import { omit } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { debounceTime, filter, fromEvent, map, of, switchMap, tap } from 'rxjs';

import {
  useCustomEventEmitter,
  useFlag,
  useResizeObserver,
  useSubscriptions,
} from '@/hook';

import { type VBaseTrackType, useVBaseTrack } from '../VBaseTrack';
import { VSliderContext } from './context';
import { twStyle } from './style';
import type {
  VSliderContextProviderEventHandlerMap,
  VSliderProps,
} from './type';
import {
  createMarkerBuffer,
  makeNormalizedMarkerItemMap,
  makeNormalizedValue,
} from './util';

export const VSliderContextProvider = <T extends VBaseTrackType>({
  children,
  marker,
  variant,
}: React.PropsWithChildren<Pick<VSliderProps<T>, 'marker' | 'variant'>>) => {
  const baseTrackContextValue = useVBaseTrack<T>();
  const {
    disabled,
    max,
    min,
    readOnly,
    step: _step,
    type,
    value,
  } = baseTrackContextValue;
  const {
    value: hasScroll,
    setFalse: scrollOff,
    setTrue: scrollOn,
  } = useFlag(false);
  const [renderTrigger, setRenderTrigger] = useState<number>();
  const step = useMemo(() => Math.max(_step, 1), [_step]);
  const markerBuffer = useMemo(
    () => createMarkerBuffer({ max, min }),
    [max, min],
  );
  const { eventEmitter } =
    useCustomEventEmitter<VSliderContextProviderEventHandlerMap>({
      onThumbMove() {},
      onTrackMarkerClick() {},
    });
  const $container = useRef<HTMLDivElement>(null);
  const visibleRangeOffsetRef = useRef<[number, number]>([0, 0]);

  const normalizedMarkerItemMap = makeNormalizedMarkerItemMap({
    $container,
    markerBuffer,
    marker,
    min,
    step,
    renderTrigger,
    visibleRangeOffsetRef,
  });

  const normalizedValue = makeNormalizedValue({
    type: type as T,
    value,
    min,
    step,
  });
  const style = twStyle({ disabled, readOnly, variant });

  useResizeObserver($container, entryList => {
    entryList.forEach(entry => {
      if (entry.target !== $container.current) return;

      const { offsetWidth, scrollWidth } = entry.target as HTMLElement;

      if (offsetWidth < scrollWidth) scrollOn();
      else scrollOff();
    });
  });

  useSubscriptions(
    () => [
      of(visibleRangeOffsetRef.current)
        .pipe(
          filter(() => {
            if (!hasScroll) {
              setRenderTrigger(Date.now());

              return false;
            }

            return true;
          }),
          map(prevRangeOffset => {
            const _$container = $container.current!;

            const containerOffsetWidth = _$container.offsetWidth;
            const scrollLeft = _$container.scrollLeft;

            visibleRangeOffsetRef.current = prevRangeOffset = [
              Math.max(scrollLeft - containerOffsetWidth * 2, 0),
              Math.min(
                scrollLeft + containerOffsetWidth * 2,
                _$container.scrollWidth,
              ),
            ];

            return { prevRangeOffset, $container: _$container };
          }),
          switchMap(({ prevRangeOffset, $container }) => {
            return fromEvent($container, 'scroll').pipe(
              debounceTime(15),
              filter(() => {
                if (
                  $container.scrollLeft >
                  prevRangeOffset[1] - $container.offsetWidth
                )
                  return true;
                if (
                  $container.scrollLeft <
                  prevRangeOffset[0] + $container.offsetWidth
                )
                  return true;

                return false;
              }),
              tap(() => {
                const containerOffsetWidth = $container.offsetWidth;
                const scrollLeft = $container.scrollLeft;

                visibleRangeOffsetRef.current = prevRangeOffset = [
                  Math.max(scrollLeft - containerOffsetWidth * 2, 0),
                  scrollLeft + containerOffsetWidth * 2,
                ];

                prevRangeOffset = visibleRangeOffsetRef.current;
              }),
              tap(() => setRenderTrigger(Date.now())),
            );
          }),
        )
        .subscribe(),
    ],
    [hasScroll],
  );

  useEffect(() => {
    if (!$container.current) return;

    $container.current.dispatchEvent(new Event('scroll'));
  }, [step]);

  useEffect(() => {
    if (!$container.current) return;

    const { offsetWidth, scrollLeft, scrollWidth } = $container.current;

    visibleRangeOffsetRef.current = [
      Math.max(scrollLeft - offsetWidth * 2, 0),
      Math.min(scrollLeft + offsetWidth * 2, scrollWidth),
    ];
  });

  return (
    <VSliderContext.Provider
      value={{
        eventEmitter: eventEmitter.current,
        normalizedMarkerItemMap,
        normalizedValue,
        marker,
        style,
        variant,
        $container,
        ...omit(baseTrackContextValue, 'eventEmitter', 'onChange'),
      }}
    >
      {children}
    </VSliderContext.Provider>
  );
};

VSliderContextProvider.displayName = 'VSliderContextProvider';
